import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { patch } from 'api/base';
import { PAUSED_CONSULTATIONS_QUERY_KEY } from 'hooks/usePausedConsultations';
import { getApiErrorMessage, parseApiError } from 'utils/apiError';
import { trackError } from 'utils/trackError';

export type InteractionStatus = 'discarded';

export interface UpdateInteractionStatusInput {
  interactionId: string;
  status: InteractionStatus;
}

export function useUpdateInteractionStatus() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ interactionId, status }: UpdateInteractionStatusInput) =>
      patch('conversations', { status }, [interactionId]) as Promise<{
        status: InteractionStatus;
        pausedAt: string | null;
        discardedAt: string | null;
      }>,
    retry: (failureCount, error) => {
      const { status } = parseApiError(error);
      if (status !== undefined && status < 500) return false;
      return failureCount < 2;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PAUSED_CONSULTATIONS_QUERY_KEY }),
    onError: (error) => {
      trackError(error, { context: 'updateInteractionStatus' });
      toast.error(getApiErrorMessage(error, t));
    },
  });
}
